import React, {useContext, useState} from "react";
import {DialogTitle, DialogContent, Tab, Tabs} from "@mui/material";
import {useAsyncAction} from "nate-react-api-helpers";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {TabWrapper} from "../TabWrapper";
import {useTableData} from "../../../../misc/scroller/Table";
import {PrintManagerContext} from "../../../../misc/pdf/PrintManager";
import {useSubmittalPreferences} from "../../../../misc/pdf/useSubmittalPreferences";
import {api} from "../../../../api/API";
import {Opening} from "../../../../api/Openings";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {RenderingStatus} from "../../../../api/ElevationTool";
import {useProject, useProjectId} from "../ProjectName";
import {useIsQuote} from "../../quote/QuoteContainer";
import {useAlternative} from "../../quote/alternative/Alternative";
import {useCurrentContractor} from "../../quote/CurrentContractor";
import {currentTab, openingsTableName} from "../openings/Openings";
import {currentQuoteTab, quoteOpeningsTableName} from "../../quote/openings/QuoteOpenings";
import {PreambleTab} from "../openings/pdf/PreambleTab";
import {SubmittalRenderGate} from "../openings/SubmittalRenderGate";
import {getGlazingSubmittal} from "./GlazingSubmittalLoader";
import {defaultGlazingPages, GlazingPageItem, GlazingPagesTab} from "./GlazingPagesTab";

// the saved configuration for this submittal.
export type GlazingSubmittalPrefs = {
    version: 1;
    notes: string;
    pages: GlazingPageItem[];
};

// configure + generate the glazing submittal: the glass each elevation variant needs, drawn beside
// the elevation it was measured on. The panels come off the elevation renderings, so generating is
// gated on those being up to date (see SubmittalRenderGate).
export function GlazingSubmittal(props: {
    onDone(): void;
    // the openings to report on. Defaults to the openings grid's current rows; the Glazing
    // workspace passes the openings its rows cover, since that grid isn't mounted there.
    openings?: number[];
}) {
    const projectId = useProjectId();
    const project = useProject();
    const isQuote = useIsQuote();
    const alternative = useAlternative();
    const printCtx = useContext(PrintManagerContext);

    const gridOpenings = useTableData(isQuote
        ? quoteOpeningsTableName(projectId, currentQuoteTab)
        : openingsTableName(projectId, currentTab)) as (Opening|QuoteOpening)[];

    const openingIds = props.openings ?? gridOpenings.map(o => o.id);

    const [tab, setTab] = useState("pages");
    const [notes, setNotes] = useState("");
    const [pages, setPages] = useState<GlazingPageItem[]>(defaultGlazingPages);
    const [renderGate, setRenderGate] = useState<RenderingStatus | null>(null);

    const prefs = useSubmittalPreferences({
        project: projectId,
        kind: "glazing",
        onLoad: (list: GlazingSubmittalPrefs) => {
            if(!("version" in list) || list.version !== 1) return;

            setNotes(list.notes);

            // preferences saved before the sections were selectable carry no page list.
            if(Array.isArray(list.pages)) setPages(list.pages);
        }
    });

    const {getContractorDetails, CurrentContractorPromptElement} = useCurrentContractor({
        onAutoPromptCanceled: props.onDone
    });

    const doGenerate = async () => {
        if(!project) return;

        await prefs.save({version: 1, notes: notes, pages: pages});
        const {client, clientContact} = await getContractorDetails();

        await printCtx.print2(getGlazingSubmittal({
            projectId: project.id,
            getData: async () => ({
                client,
                clientContact,
                project,
                notes,
                pages,
                lastSubmission: prefs.lastSubmission,
                openings: openingIds,
                alternative: isQuote ? alternative : undefined,
            }),
        }));

        props.onDone();
    };

    // declared before the early returns below so this hook runs on every render.
    const submit = useAsyncAction(async () => {
        if(!project) return;

        // re-render (in place) any rendering left by an older render version first: a rendering from
        // before the glass measurements were reported carries none, so its section would come out
        // with an empty table.
        await api.elevationTool.refreshRenderings({project: project.id});

        const status = await api.elevationTool.renderingStatus({project: project.id});
        if(status.pending > 0 || status.failed > 0) {
            setRenderGate(status);
            return;
        }

        await doGenerate();
    }, [project, doGenerate]);

    // generate after the user chose to proceed past pending/failed renders
    const proceed = useAsyncAction(async () => {
        await doGenerate();
    }, [doGenerate]);

    if(CurrentContractorPromptElement) {
        return (<>{CurrentContractorPromptElement}</>);
    }

    if(prefs.loading) return null;

    return (
        <>
            <Dialog2 open maxWidth="md" fullWidth onClose={props.onDone}>
                <DialogTitle>Create Glazing Submittal</DialogTitle>
                <TabWrapper>
                    <Tabs className={tabGroup} value={tab} onChange={(e, value) => setTab(value)}>
                        <Tab label="Pages" value="pages" />
                        <Tab label="Preamble" value="preamble" />
                    </Tabs>
                </TabWrapper>
                <DialogContent>
                    {tab === "pages" ?
                        <GlazingPagesTab value={pages} onChange={setPages} /> :
                        <PreambleTab value={notes} onChange={setNotes} />}
                </DialogContent>
                <SmoothActionBar
                    loading={submit.loading || proceed.loading}
                    error={submit.error || proceed.error}
                    onCancel={props.onDone}
                    onSubmit={() => submit.callback(undefined)}
                    actionName="Create and Download"
                    submittingWord="Creating…"
                />
            </Dialog2>
            {renderGate && project && <SubmittalRenderGate
                projectId={project.id}
                status={renderGate}
                onProceed={() => {
                    setRenderGate(null);
                    proceed.callback(undefined);
                }}
                onCancel={() => setRenderGate(null)}
            />}
        </>
    );
}
